import { useState } from 'react'

import cn from 'classnames'
import { Loader2 } from 'lucide-react'

import CopyToClipboard from '@src/components/CopyToClipboard'
import ChevronDownIcon from '@src/components/icons/ChevronDown'
import FuelIcon from '@src/components/icons/Fuel'
import InfoIcon from '@src/components/icons/Info'
import { ChainId } from '@src/configs/chainInfo'
import { LiquiditySource } from '@src/enums'
import { Address, EVMAddress } from '@src/types/token'
import { isFeatureEnabled } from '@src/utils/environment'
import { formatExchangePrice } from '@src/utils/formatExchangePrice'
import { formatNumber } from '@src/utils/formatFee'
import { truncateAddress } from '@src/utils/truncateAddress'

interface DetailedRowsProps {
    destinationAddress: Address | undefined
    evmWalletAddress: EVMAddress | undefined
    isCustomRecipientWallet: boolean
    shortenedAddress: string
    sellData: any // Add proper type
    buyData: any // Add proper type
    gasFeeString: string
    precomputedGasPrices: any // Add proper type
    liquiditySource: LiquiditySource
    machFee: number
    miles: number
}

function DetailedRows({
    destinationAddress,
    evmWalletAddress,
    isCustomRecipientWallet,
    shortenedAddress,
    sellData,
    buyData,
    gasFeeString,
    precomputedGasPrices,
    liquiditySource,
    machFee,
    miles,
}: DetailedRowsProps) {
    return (
        <>
            <div className='flex justify-between'>
                <span className='text-mach-sub-text'>Destination Wallet</span>
                <div className='flex items-center gap-[6px]'>
                    <CopyToClipboard anchorId='dest-address-1' textToCopy={destinationAddress || evmWalletAddress!} />
                    <span className='text-white'>{truncateAddress(destinationAddress || evmWalletAddress!)}</span>
                </div>
            </div>

            <div className='flex justify-between'>
                <span className='text-mach-sub-text'>Rate</span>
                <span className='flex items-center text-white'>
                    1&nbsp;
                    <span className='max-w-[80px] truncate'>{sellData.token?.symbol}</span>
                    &nbsp;=&nbsp;
                    {formatExchangePrice(sellData, buyData)}&nbsp;
                    {<span className='max-w-[80px] truncate'>{buyData.token?.symbol}</span>}
                </span>
            </div>

            <div className='flex justify-between'>
                <span className='text-mach-sub-text'>Gas fees</span>
                <div className='flex flex-col items-end'>
                    <span className='text-white'>{gasFeeString}</span>
                </div>
            </div>

            <ChainSpecificGasDetails sellData={sellData} precomputedGasPrices={precomputedGasPrices} />

            <div className='flex justify-between'>
                <span className='text-mach-sub-text'>Mach fees</span>
                {liquiditySource === LiquiditySource.cctpDirect ? (
                    <span className='w-fit items-center rounded-md bg-mach-mach-green px-2 py-0.5 text-[9.5px] text-white'>
                        FREE
                    </span>
                ) : (
                    <span className='text-white'>
                        {+formatNumber(machFee) < 0.01 ? '<$0.01' : `$${formatNumber(machFee)}`}
                    </span>
                )}
            </div>

            <div className='flex justify-between'>
                <span className='text-mach-sub-text'>Miles Earned</span>
                <span className='text-white'>{miles.toLocaleString('en-us', { maximumFractionDigits: 3 })} Miles</span>
            </div>
        </>
    )
}

function ChainSpecificGasDetails({ sellData, precomputedGasPrices }: { sellData: any; precomputedGasPrices: any }) {
    if (sellData.chainId === ChainId.Tron && precomputedGasPrices) {
        const TronParams = precomputedGasPrices[ChainId.Tron].feeMetrics.TronParams
        if (TronParams) {
            return (
                <>
                    <div className='flex justify-between'>
                        <span className='text-mach-sub-text'>Energy Fee</span>
                        <span className='text-white'>{TronParams.energyPrice.toString()} Sun</span>
                    </div>
                    <div className='flex justify-between'>
                        <span className='text-mach-sub-text'>Bandwidth Fee</span>
                        <span className='text-white'>{TronParams.bandwidthPrice.toString()} Sun</span>
                    </div>
                </>
            )
        }
    }
    return null
}

interface PriceInfoProps {
    isPriceFlipped: boolean
    setIsPriceFlipped: (value: boolean) => void
    sellData: any
    buyData: any
    isWrapOrUnwrapOnly: boolean
    isBuyOutputLoading: boolean
    isQuoteLoading: boolean
}

function PriceInfo({
    isPriceFlipped,
    setIsPriceFlipped,
    sellData,
    buyData,
    isWrapOrUnwrapOnly,
    isBuyOutputLoading,
    isQuoteLoading,
}: PriceInfoProps) {
    const [showPriceTooltip, setShowPriceTooltip] = useState(false)
    const isTestnet = isFeatureEnabled('USE_TESTNETS_ONLY')

    return (
        <div className='relative flex flex-row'>
            <span
                className='flex cursor-pointer items-center transition-colors duration-300 hover:text-white'
                onClick={() => setIsPriceFlipped(!isPriceFlipped)}
            >
                1&nbsp;
                {isPriceFlipped ? (
                    <span className='max-w-[80px] truncate'>{buyData.token?.symbol}</span>
                ) : (
                    <span className='max-w-[80px] truncate'>{sellData.token?.symbol}</span>
                )}
                &nbsp;&nbsp;=&nbsp;&nbsp;
                {(() => {
                    if (isWrapOrUnwrapOnly) return 1
                    if (isBuyOutputLoading || isQuoteLoading) {
                        if (sellData.value > 0) {
                            return <Loader2 className='mr-1 inline h-4 w-4 animate-spin align-middle' />
                        }
                    }
                    const price = isPriceFlipped
                        ? formatExchangePrice(buyData, sellData)
                        : formatExchangePrice(sellData, buyData)
                    if (price === '<loading>' || price === 'Infinity') {
                        return <Loader2 className='mr-1 inline h-4 w-4 animate-spin align-middle' />
                    }
                    return price
                })()}
                &nbsp;
                {isPriceFlipped ? (
                    <span className='max-w-[80px] truncate'>{sellData.token?.symbol}</span>
                ) : (
                    <span className='max-w-[80px] truncate'>{buyData.token?.symbol}</span>
                )}
            </span>

            {isTestnet && (
                <>
                    <div
                        className='ml-2 inline-block cursor-pointer align-middle'
                        onMouseEnter={() => setShowPriceTooltip(true)}
                        onMouseLeave={() => setShowPriceTooltip(false)}
                    >
                        <InfoIcon className='h-4 w-4 text-mach-sub-text' />
                    </div>
                    {showPriceTooltip && (
                        <div className='absolute bottom-full left-0 mb-2 w-48 rounded-md bg-mach-modal-background p-2 text-xs text-white shadow-lg'>
                            Testnet prices are hardcoded and may adjust in the future.
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

interface IGasFeePanelProps {
    isGasFeeExpanded: boolean
    gasFeeString: string
    onToggleGasFee: () => void
    detailedRowsProps: DetailedRowsProps
    isPriceFlipped: boolean
    setIsPriceFlipped: (value: boolean) => void
    sellData: any
    buyData: any
    isWrapOrUnwrapOnly: boolean
    isBuyOutputLoading: boolean
    isQuoteLoading: boolean
}

export function GasFeePanel({
    isGasFeeExpanded,
    gasFeeString,
    onToggleGasFee,
    detailedRowsProps,
    isPriceFlipped,
    setIsPriceFlipped,
    sellData,
    buyData,
    isWrapOrUnwrapOnly,
    isBuyOutputLoading,
    isQuoteLoading,
}: IGasFeePanelProps) {
    const [showTooltip, setShowTooltip] = useState(false)
    const isTestnet = isFeatureEnabled('USE_TESTNETS_ONLY')

    return (
        <div className='flex flex-col rounded-[10px] border border-[#1A1B20] px-5 py-3 text-xs font-medium text-mach-sub-text'>
            <div className='flex w-full items-center justify-between'>
                <PriceInfo
                    isPriceFlipped={isPriceFlipped}
                    setIsPriceFlipped={setIsPriceFlipped}
                    sellData={sellData}
                    buyData={buyData}
                    isWrapOrUnwrapOnly={isWrapOrUnwrapOnly}
                    isBuyOutputLoading={isBuyOutputLoading}
                    isQuoteLoading={isQuoteLoading}
                />

                <div className='flex items-center gap-3'>
                    {isTestnet && (
                        <div className='relative'>
                            <div
                                className='cursor-pointer'
                                onMouseEnter={() => setShowTooltip(true)}
                                onMouseLeave={() => setShowTooltip(false)}
                            >
                                <InfoIcon className='h-4 w-4 text-mach-sub-text' />
                            </div>
                            {showTooltip && (
                                <div className='absolute bottom-full right-0 mb-2 w-48 rounded-md bg-mach-modal-background p-2 text-xs text-white shadow-lg'>
                                    Testnet gas estimations may be significantly overestimated.
                                </div>
                            )}
                        </div>
                    )}
                    <div className='flex cursor-pointer items-center gap-3' onClick={onToggleGasFee}>
                        <div className='flex items-center gap-1 rounded-md bg-mach-button-tabs-background px-2 py-1'>
                            <FuelIcon />
                            <span className='text-xs text-mach-sub-text'>{gasFeeString}</span>
                        </div>
                        <ChevronDownIcon
                            className={cn('h-4 w-4 transition-all', isGasFeeExpanded ? 'rotate-180' : '')}
                        />
                    </div>
                </div>
            </div>

            {/* Expandable content */}
            <div
                className={cn(
                    'flex flex-col space-y-2 overflow-hidden transition-all duration-500',
                    isGasFeeExpanded ? 'mt-2' : 'h-0'
                )}
            >
                {isGasFeeExpanded && <DetailedRows {...detailedRowsProps} />}
            </div>
        </div>
    )
}
